import React, { useEffect, useState } from 'react';
import unitReportService, {
  UnitGridData,
  WingGridData,
  UnitCellData,
  UnitTypeSummary,
  GridStats,
} from '../services/unitReportService';
import '../css/pages/unitReportGrid.css';

interface UnitReportGridProps {
  projectId: string;
  projectName?: string;
  userProjects: Array<{ id: number; name: string }>;
}

const UnitReportGrid: React.FC<UnitReportGridProps> = ({ projectId, projectName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<'wing' | 'unitType'>('wing');
  const [gridData, setGridData] = useState<UnitGridData | null>(null);
  const [selectedWings, setSelectedWings] = useState<string[]>([]);
  const [selectedUnitType, setSelectedUnitType] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'booked' | 'unsold' | 'hold'>('all');
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');

  const [selectedCell, setSelectedCell] = useState<UnitCellData | null>(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const reloadGridData = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      setError(null);
      const wingsParam = viewMode === 'wing' && selectedWings.length > 0
        ? selectedWings.join(',')
        : undefined;
      const unitTypeParam = viewMode === 'unitType' && selectedUnitType
        ? selectedUnitType
        : undefined;
      const data = await unitReportService.getUnitGridData(
        projectId, wingsParam, unitTypeParam, statusFilter,
        selectedYear || undefined, selectedMonth || undefined
      );
      setGridData(data);
      if (viewMode === 'wing' && selectedWings.length === 0 && data.wings.length > 0) {
        setSelectedWings(data.wings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load unit data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reloadGridData();
  }, [projectId, statusFilter, viewMode, selectedUnitType, selectedYear, selectedMonth]);

  useEffect(() => {
    if (viewMode === 'wing') {
      reloadGridData();
    }
  }, [selectedWings]);

  const handleViewModeChange = (mode: 'wing' | 'unitType') => {
    setViewMode(mode);
    if (mode === 'wing') {
      setSelectedUnitType(null);
    }
  };

  const toggleWing = (wing: string) => {
    setSelectedWings(prev =>
      prev.includes(wing) ? prev.filter(w => w !== wing) : [...prev, wing]
    );
  };

  const handleCellClick = (unit: UnitCellData, event: React.MouseEvent) => {
    setSelectedCell(unit);
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setTooltipPos({
      x: Math.min(rect.left, window.innerWidth - 280),
      y: Math.min(rect.bottom + 4, window.innerHeight - 200),
    });
  };

  const closeTooltip = () => setSelectedCell(null);

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'unsold': return 'Unsold';
      case 'sold': return 'Sold';
      case 'booked': return 'Booked';
      case 'hold': return 'Hold';
      default: return status;
    }
  };

  if (loading) {
    return <div className="grid-loading">Loading unit grid...</div>;
  }

  if (error) {
    return <div className="grid-error">{error}</div>;
  }

  if (!gridData) {
    return <div className="grid-empty">No data to display</div>;
  }

  return (
    <div className="unit-report-grid">
      <div className="grid-toolbar">
        <div className="view-mode-toggle">
          <button
            className={`view-mode-btn ${viewMode === 'wing' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('wing')}
          >
            By Wing
          </button>
          <button
            className={`view-mode-btn ${viewMode === 'unitType' ? 'active' : ''}`}
            onClick={() => handleViewModeChange('unitType')}
          >
            By Unit Type
          </button>
        </div>

        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-value">{gridData.stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat-item stat-unsold">
            <span className="stat-value">{gridData.stats.unsold}</span>
            <span className="stat-label">Unsold</span>
          </div>
          <div className="stat-item stat-booked">
            <span className="stat-value">{gridData.stats.booked}</span>
            <span className="stat-label">Booked</span>
          </div>
          <div className="stat-item stat-hold">
            <span className="stat-value">{gridData.stats.hold}</span>
            <span className="stat-label">Hold</span>
          </div>
        </div>

        <div className="status-filter-toggle">
          {(['all', 'unsold', 'booked', 'hold'] as const).map(f => (
            <button
              key={f}
              className={`status-filter-btn ${statusFilter === f ? 'active' : ''}`}
              onClick={() => setStatusFilter(f)}
            >
              {f === 'all' ? 'All' : getStatusLabel(f)}
            </button>
          ))}
        </div>
      </div>

      {gridData.bookingTimeline && gridData.bookingTimeline.length > 0 && (
        <div className="timeline-bar">
          <span className="timeline-label">Booking Period:</span>
          <select
            className="timeline-select"
            value={selectedYear}
            onChange={(e) => { setSelectedYear(e.target.value); setSelectedMonth(''); }}
          >
            <option value="">All Years</option>
            {gridData.bookingTimeline.map(ty => (
              <option key={ty.year} value={ty.year}>{ty.year} ({ty.count})</option>
            ))}
          </select>
          {selectedYear && (
            <select
              className="timeline-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
            >
              <option value="">All Months</option>
              {gridData.bookingTimeline
                .find(ty => ty.year === parseInt(selectedYear))
                ?.months.map(tm => (
                  <option key={tm.month} value={tm.month}>{tm.label} ({tm.count})</option>
                ))}
            </select>
          )}
          {(selectedYear || selectedMonth) && (
            <button
              className="timeline-clear"
              onClick={() => { setSelectedYear(''); setSelectedMonth(''); }}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {viewMode === 'wing' && (
        <div className="filter-chips">
          {gridData.wings.map(wing => (
            <button
              key={wing}
              className={`filter-chip ${selectedWings.includes(wing) ? 'selected' : ''}`}
              onClick={() => toggleWing(wing)}
            >
              Wing {wing}
            </button>
          ))}
        </div>
      )}

      {viewMode === 'unitType' && (
        <div className="filter-chips">
          {gridData.unitTypes.map(ut => (
            <button
              key={ut.type}
              className={`filter-chip ${selectedUnitType === ut.type ? 'selected' : ''}`}
              onClick={() => setSelectedUnitType(prev => prev === ut.type ? null : ut.type)}
            >
              {ut.type} <span className="chip-count">{ut.total}</span>
              {ut.unsold > 0 && <span className="chip-sub unsold">{ut.unsold}</span>}
              {ut.booked > 0 && <span className="chip-sub booked">{ut.booked}</span>}
            </button>
          ))}
        </div>
      )}

      {gridData.wingGridData.length === 0 && (
        <div className="grid-no-results">No units found for the selected filters</div>
      )}

      {gridData.wingGridData.map(wg => (
        <div key={wg.wing} className="wing-section">
          <div className="wing-header">Wing {wg.wing}</div>
          {wg.floors.map(floor => {
            const maxPos = Math.max(...floor.units.map(u => (u as any).position || 0));
            const positions = new Set(floor.units.map(u => (u as any).position || 0));
            const cells: (UnitCellData | null)[] = [];
            for (let p = 1; p <= maxPos; p++) {
              const unit = floor.units.find(u => (u as any).position === p);
              cells.push(unit || null);
            }

            return (
              <div key={floor.floor} className="floor-row">
                <div className="floor-label">F{floor.floor}</div>
                <div className="floor-units-grid">
                  {cells.map((unit, idx) => {
                    if (!unit) {
                      return <div key={idx} className="unit-cell empty" />;
                    }
                    const statusClass =
                      unit.status === 'unsold' ? 'status-unsold' :
                      unit.status === 'hold' ? 'status-hold' : 'status-booked';
                    return (
                      <div
                        key={unit.id}
                        className={`unit-cell ${statusClass}`}
                        onClick={(e) => handleCellClick(unit, e)}
                      >
                        <span className="unit-no">{unit.unitNo}</span>
                        <span className="unit-type">{unit.unitType}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      ))}

      {selectedCell && (
        <>
          <div className="tooltip-overlay" onClick={closeTooltip} />
          <div className="unit-tooltip" style={{ left: tooltipPos.x, top: tooltipPos.y }}>
            <button className="tooltip-close" onClick={closeTooltip}>&times;</button>
            <div className="tooltip-header">{selectedCell.unitNo}</div>
            <div className="tooltip-body">
              <div><label>Type:</label> {selectedCell.unitType}</div>
              <div><label>Size:</label> {selectedCell.size}</div>
              <div>
                <label>Status:</label>
                <span className={`tooltip-status ${selectedCell.status === 'unsold' ? 'unsold' : selectedCell.status === 'hold' ? 'hold' : 'booked'}`}>
                  {getStatusLabel(selectedCell.status)}
                </span>
              </div>
              {selectedCell.bookingId && (
                <div><label>Booking:</label> {selectedCell.bookingId}</div>
              )}
              {selectedCell.customerName && (
                <div><label>Customer:</label> {selectedCell.customerName}</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UnitReportGrid;
